import { useAuth } from "@/hooks/useAuth";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";
import { Link } from "react-router-dom";
import { AlertCircle, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

export function ProfileCompletionBanner() {
  const { tenant, user } = useAuth();
  const { completionPercentage, isPublishable } = useProfileCompletion(tenant);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when user changes or on new session
  useEffect(() => {
    if (user?.id) {
      const dismissedKey = `profile-banner-dismissed-${user.id}`;
      const isDismissed = sessionStorage.getItem(dismissedKey);
      setDismissed(isDismissed === "true");
    }
  }, [user?.id]);

  const handleDismiss = () => {
    if (user?.id) {
      sessionStorage.setItem(`profile-banner-dismissed-${user.id}`, "true");
    }
    setDismissed(true);
  };

  // Don't show if:
  // - No tenant yet
  // - Profile is complete (100%)
  // - User dismissed this session
  if (!tenant || isPublishable || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-yellow-500 dark:bg-yellow-600 text-yellow-950 px-4 py-3 shadow-lg">
      <div className="container mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm font-medium">
            Your profile is {completionPercentage}% complete. 
            <span className="hidden sm:inline"> Complete it to get listed on the public VLE directory.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            asChild 
            size="sm" 
            variant="secondary"
            className="bg-yellow-950 text-yellow-100 hover:bg-yellow-900"
          >
            <Link to="/settings">Complete Profile</Link>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-yellow-950 hover:bg-yellow-600"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
